import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Select, Typography, Spin, Empty, Avatar } from 'antd';
import {
  UserOutlined,
  TrophyOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  TeamOutlined
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { userService } from '../services/userService';
import useAuthStore from '../store/authStore';
import useSettingsStore from '../store/settingsStore';
import api from '../services/apiService';

const { Title, Text } = Typography;
const { Option } = Select;

const SalesPersonAnalytics = () => {
  const { user } = useAuthStore();
  const formatCurrency = useSettingsStore((s) => s.formatCurrency);
  const currencySymbol = useSettingsStore((s) => s.currencySymbol);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Fetch team members (for tenant admin)
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await userService.getTeamMembers();
        console.log('Team members response:', response);
        
        // Handle different response structures
        let members = [];
        if (response.success && response.data) {
          // Check if data has teamMembers property
          if (response.data.teamMembers && Array.isArray(response.data.teamMembers)) {
            members = response.data.teamMembers.map(member => ({
              ...member,
              _id: member.id || member._id, // Handle both id and _id
              firstName: member.name ? member.name.split(' ')[0] : member.firstName || '',
              lastName: member.name ? member.name.split(' ').slice(1).join(' ') : member.lastName || ''
            }));
          } else if (Array.isArray(response.data)) {
            members = response.data;
          }
        } else if (Array.isArray(response)) {
          members = response;
        }
        
        setTeamMembers(members);
        
        // Set default selected user
        console.log('User role:', user?.role);
        console.log('Members count:', members.length);
        if (user?.role === 'tenant_admin' && members.length > 0) {
          console.log('Setting selected user (admin):', members[0]._id);
          setSelectedUser(members[0]._id);
        } else if (user?.role === 'crm_user') {
          console.log('Setting selected user (crm_user):', user._id);
          setSelectedUser(user._id);
        } else {
          console.log('No user selected - role or members issue');
        }
      } catch (error) {
        console.error('Failed to fetch team members:', error);
        setTeamMembers([]); // Ensure it's always an array
      }
    };

    if (user) {
      fetchTeamMembers();
    }
  }, [user]);

  // Fetch analytics data when user is selected
  useEffect(() => {
    console.log('Selected user changed:', selectedUser);
    if (selectedUser) {
      fetchAnalytics(selectedUser);
    } else {
      console.log('No selected user, skipping analytics fetch');
    }
  }, [selectedUser]);

  const fetchAnalytics = async (userId) => {
    setLoading(true);
    try {
      console.log('Fetching analytics for userId:', userId);
      const response = await api.get(`/analytics/salesperson/${userId}`);
      console.log('Analytics response:', response.data);
      if (response.data.success) {
        setAnalytics(response.data.data);
      } else {
        console.error('Analytics fetch unsuccessful:', response.data);
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Failed to fetch sales person analytics:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedUserInfo = () => {
    if (!selectedUser || !Array.isArray(teamMembers)) return null;
    return teamMembers.find(member => member._id === selectedUser);
  };

  const selectedUserInfo = getSelectedUserInfo();

  // Prepare chart data
  const leadStatusData = analytics?.leadsByStatus?.map((item, index) => ({
    name: item._id ? item._id.replace('_', ' ').toUpperCase() : 'Unknown',
    value: item.count,
    fill: COLORS[index % COLORS.length]
  })) || [];

  const dealStageData = analytics?.dealsByStage?.map((item, index) => ({
    name: item._id ? item._id.replace('_', ' ').toUpperCase() : 'Unknown',
    value: item.count,
    totalValue: item.totalValue || 0,
    fill: COLORS[index % COLORS.length]
  })) || [];

  const monthlyTrendData = analytics?.monthlyTrends || [];

  return (
    <div className="space-y-6">
      {/* User Selection Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title level={3} className="mb-2">Sales Person Analytics</Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Individual performance metrics and insights
          </Text>
        </div>
        
        {user?.role === 'tenant_admin' && (
          <Select
            style={{ width: 300 }}
            placeholder="Select team member"
            value={selectedUser}
            onChange={setSelectedUser}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {Array.isArray(teamMembers) && teamMembers.map(member => (
              <Option key={member._id} value={member._id}>
                <div className="flex items-center space-x-2">
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span>
                    {member.firstName} {member.lastName} ({member.role})
                  </span>
                </div>
              </Option>
            ))}
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : !analytics ? (
        <Card>
          <Empty description="No analytics data available" />
        </Card>
      ) : (
        <>
          {/* Selected User Info Card */}
          {selectedUserInfo && (
            <Card className="shadow-soft bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900">
              <div className="flex items-center space-x-4">
                <Avatar size={64} icon={<UserOutlined />} className="bg-blue-500" />
                <div>
                  <Title level={4} className="mb-1">
                    {selectedUserInfo.firstName} {selectedUserInfo.lastName}
                  </Title>
                  <Text className="text-gray-600 dark:text-gray-400">
                    {selectedUserInfo.email} • {selectedUserInfo.role === 'tenant_admin' ? 'Tenant Admin' : 'CRM User'}
                  </Text>
                </div>
              </div>
            </Card>
          )}

          {/* Key Performance Metrics */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="Total Leads Created"
                  value={analytics.totalLeads || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Active: {analytics.activeLeads || 0}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="Total Deals Created"
                  value={analytics.totalDeals || 0}
                  prefix={<TrophyOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Won: {analytics.wonDeals || 0}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="Total Revenue"
                  value={analytics.totalRevenue || 0}
                  formatter={(v) => formatCurrency(v)}
                  valueStyle={{ color: '#3f8600' }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Avg Deal: {formatCurrency(Math.round(analytics.avgDealSize || 0))}
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="Conversion Rate"
                  value={analytics.conversionRate || 0}
                  suffix="%"
                  prefix={analytics.conversionRate > 50 ? <RiseOutlined /> : <FallOutlined />}
                  valueStyle={{ color: analytics.conversionRate > 50 ? '#3f8600' : '#cf1322' }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Converted: {analytics.convertedLeads || 0}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Additional Metrics */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="This Month Leads"
                  value={analytics.thisMonthLeads || 0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="This Month Deals"
                  value={analytics.thisMonthDeals || 0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="Pipeline Value"
                  value={analytics.pipelineValue || 0}
                  formatter={(v) => formatCurrency(v)}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-soft">
                <Statistic
                  title="Win Rate"
                  value={analytics.winRate || 0}
                  suffix="%"
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={[16, 16]}>
            {/* Leads by Status */}
            <Col xs={24} lg={12}>
              <Card title="Leads by Status" className="shadow-soft">
                {leadStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={leadStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {leadStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="No lead data available" />
                )}
              </Card>
            </Col>

            {/* Deals by Stage */}
            <Col xs={24} lg={12}>
              <Card title="Deals by Stage" className="shadow-soft">
                {dealStageData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dealStageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'totalValue' ? formatCurrency(value) : value,
                          name === 'totalValue' ? 'Total Value' : 'Deal Count'
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="value" fill="#8884d8" name="Deal Count" />
                      <Bar dataKey="totalValue" fill="#82ca9d" name="Total Value" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="No deal data available" />
                )}
              </Card>
            </Col>
          </Row>

          {/* Monthly Trends */}
          {monthlyTrendData.length > 0 && (
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Card title="Monthly Performance Trends" className="shadow-soft">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="leads"
                        stroke="#8884d8"
                        strokeWidth={2}
                        name="Leads Created"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="deals"
                        stroke="#82ca9d"
                        strokeWidth={2}
                        name="Deals Created"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenue"
                        stroke="#ffc658"
                        strokeWidth={2}
                        name={`Revenue (${currencySymbol})`}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          )}

          {/* Performance Summary */}
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card title="Performance Summary" className="shadow-soft">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <div className="p-4 bg-blue-50 dark:bg-gray-700 rounded-lg">
                      <Text className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Lead Performance
                      </Text>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Text>Total Created:</Text>
                          <Text strong>{analytics.totalLeads || 0}</Text>
                        </div>
                        <div className="flex justify-between">
                          <Text>Converted:</Text>
                          <Text strong className="text-green-600">{analytics.convertedLeads || 0}</Text>
                        </div>
                        <div className="flex justify-between">
                          <Text>Active:</Text>
                          <Text strong className="text-blue-600">{analytics.activeLeads || 0}</Text>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} md={8}>
                    <div className="p-4 bg-green-50 dark:bg-gray-700 rounded-lg">
                      <Text className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Deal Performance
                      </Text>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Text>Total Created:</Text>
                          <Text strong>{analytics.totalDeals || 0}</Text>
                        </div>
                        <div className="flex justify-between">
                          <Text>Won:</Text>
                          <Text strong className="text-green-600">{analytics.wonDeals || 0}</Text>
                        </div>
                        <div className="flex justify-between">
                          <Text>Active:</Text>
                          <Text strong className="text-blue-600">{analytics.activeDeals || 0}</Text>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} md={8}>
                    <div className="p-4 bg-purple-50 dark:bg-gray-700 rounded-lg">
                      <Text className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Revenue Performance
                      </Text>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <Text>Total Revenue:</Text>
                          <Text strong className="text-green-600">
                            {formatCurrency(analytics.totalRevenue || 0)}
                          </Text>
                        </div>
                        <div className="flex justify-between">
                          <Text>Avg Deal Size:</Text>
                          <Text strong>
                            {formatCurrency(Math.round(analytics.avgDealSize || 0))}
                          </Text>
                        </div>
                        <div className="flex justify-between">
                          <Text>Pipeline Value:</Text>
                          <Text strong className="text-purple-600">
                            {formatCurrency(analytics.pipelineValue || 0)}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default SalesPersonAnalytics;
